import { create } from 'zustand';
import { api } from '../services/api';

export interface PollOption {
  id: string;
  label: string;
  sort_order: number;
  vote_count: number;
  user_voted: boolean;
  voter_ids?: string[];
}

export interface Poll {
  id: string;
  family_id: string;
  title: string;
  description: string;
  poll_type: string;
  options: PollOption[];
  total_votes: number;
  user_has_voted: boolean;
  created_by: string;
  creator_name: string;
  expires_at: string | null;
  closed_at: string | null;
  created_at: string;
}

interface PollsState {
  polls: Poll[];
  isLoading: boolean;
  error: string | null;
  fetchPolls: () => Promise<void>;
  createPoll: (data: { title: string; description?: string; poll_type?: string; options: string[]; expires_at?: string }) => Promise<void>;
  castVote: (pollId: string, optionId: string) => Promise<void>;
  deletePoll: (pollId: string) => Promise<void>;
  closePoll: (pollId: string) => Promise<void>;
  handleWsMessage: (message: any) => void;
}

export const usePollsStore = create<PollsState>((set, get) => ({
  polls: [],
  isLoading: false,
  error: null,

  fetchPolls: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get('/polls');
      set({ polls: res.data.polls, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createPoll: async (data) => {
    try {
      const res = await api.post('/polls', data);
      const newPoll = res.data.poll;
      set(state => {
        if (state.polls.some(p => p.id === newPoll.id)) return state;
        return { polls: [newPoll, ...state.polls] };
      });
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  castVote: async (pollId, optionId) => {
    await api.post(`/polls/${pollId}/vote`, { option_id: optionId });
    // Optimistically update the local state before refetch
    set(state => ({
      polls: state.polls.map(p => {
        if (p.id !== pollId) return p;
        return {
          ...p,
          user_has_voted: true,
          total_votes: p.total_votes + 1,
          options: p.options.map(opt => opt.id === optionId
            ? { ...opt, vote_count: opt.vote_count + 1, user_voted: true }
            : opt
          ),
        };
      }),
    }));
    // Also refetch to get accurate server data
    try { await get().fetchPolls(); } catch { /* optimistic state is good enough */ }
  },

  deletePoll: async (pollId) => {
    try {
      await api.delete(`/polls/${pollId}`);
      set(state => ({ polls: state.polls.filter(p => p.id !== pollId) }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  closePoll: async (pollId) => {
    try {
      await api.put(`/polls/${pollId}/close`);
      set(state => ({
        polls: state.polls.map(p => p.id === pollId ? { ...p, closed_at: new Date().toISOString() } : p),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  handleWsMessage: (message) => {
    const { type, payload } = message;
    if (type === 'POLL_CREATED') {
      set(state => {
        if (state.polls.some(p => p.id === payload.id)) return state;
        return { polls: [payload, ...state.polls] };
      });
    } else if (type === 'POLL_VOTED') {
      // Refetch for accurate counts
      get().fetchPolls();
    } else if (type === 'POLL_DELETED') {
      set(state => ({ polls: state.polls.filter(p => p.id !== payload.pollId) }));
    } else if (type === 'POLL_CLOSED') {
      set(state => ({
        polls: state.polls.map(p => p.id === payload.pollId ? { ...p, closed_at: payload.closed_at || new Date().toISOString() } : p),
      }));
    }
  },
}));
